import { type CSSProperties, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Drawer, DrawerBadge } from '@helios/ui/overlay';
import { IntakeRenderer, validateQuestionnaire } from '@helios/ui/intake';
import type {
  IntakeQuestionnaire,
  IntakeAnswers,
  ValidationError,
  Section,
  Question,
  FieldType,
  Option,
} from '@helios/ui/intake';
import { NavIcon } from '../shell/shared';
import { useBrandTokens } from '../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import {
  qIntakeFormDefinitions,
  qIntakeFormDefinitionById,
  buildIntakeDefinitionVM,
  serializeIntakeDefinitionForWrite,
  type IntakeFormDefinitionRow,
  type IntakeFormDefinitionVM,
} from '@helios/data-access';
import type { FieldValidation, Rule, ValidationSchema } from '@helios/validation';
import { ValidationControls } from '../components/ValidationControls';

// ─── View state ───

type ViewState =
  | { mode: 'list' }
  | { mode: 'edit'; definitionId: string }
  | { mode: 'view'; definitionId: string };

// ─── Constants ───

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'multi-select', label: 'Multi-Select' },
  { value: 'yes-no', label: 'Yes / No' },
  { value: 'signature', label: 'Signature' },
];

type Selected =
  | { type: 'section'; sectionId: string }
  | { type: 'question'; sectionId: string; questionId: string }
  | null;

// ─── ID helpers ───

let idCounter = 0;
function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${(++idCounter).toString(36)}`;
}

function now(): string {
  return new Date().toISOString();
}

// ─── Version helpers ───
//
// `version` is an epoch-millis timestamp generated on the client so that
// concurrent offline edits/copies of the same form never collide on the
// (name, version) pair. This replaces the old auto-incrementing counter,
// which produced duplicate keys when two drafts were created offline.
function versionStamp(): number {
  return Date.now();
}

// Legacy rows still hold small integer counters (1, 2, 3…). Anything below
// this threshold (~Sep 2001 in epoch ms) is treated as a legacy counter and
// shown as "Version N"; timestamps are rendered as a human date-time.
const VERSION_TIMESTAMP_THRESHOLD = 1_000_000_000_000;

function formatVersion(version: number): string {
  if (version < VERSION_TIMESTAMP_THRESHOLD) return `Version ${version}`;
  return new Date(version).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

// ─── Shared Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--text, #111827)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height, 42px)',
  padding: '0 var(--input-padding-x, 16px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border, #e5e7eb)',
  background: 'var(--input-bg, #ffffff)',
  fontSize: 'var(--font-size-base, 14px)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--input-text, #0a0a0a)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaBaseStyle: CSSProperties = {
  ...inputStyle,
  height: 'auto',
  minHeight: 64,
  padding: '10px var(--input-padding-x, 16px)',
  resize: 'vertical',
};

const smallBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  color: 'var(--text-muted, #6b7280)',
  borderRadius: 'var(--radius-sm, 6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// ─── Drag Handle (6-dot grip) ───

function GripIcon() {
  return (
    <svg
      width="10"
      height="16"
      viewBox="0 0 10 16"
      fill="currentColor"
      style={{ display: 'block' }}
    >
      <circle cx="2.5" cy="2" r="1.5" />
      <circle cx="7.5" cy="2" r="1.5" />
      <circle cx="2.5" cy="8" r="1.5" />
      <circle cx="7.5" cy="8" r="1.5" />
      <circle cx="2.5" cy="14" r="1.5" />
      <circle cx="7.5" cy="14" r="1.5" />
    </svg>
  );
}

// ─── Type Icon ───

function TypeIcon({ type }: { type: FieldType }) {
  const names: Record<FieldType, string> = {
    text: 'edit',
    textarea: 'file-text',
    number: 'bar-chart',
    date: 'calendar',
    select: 'list',
    'multi-select': 'list',
    'yes-no': 'check-circle',
    signature: 'edit',
  };
  return <NavIcon name={names[type] ?? 'edit'} />;
}

// ─── BuilderHeader ───

function BuilderHeader({
  questionnaire,
  errors,
  definitionName,
  saving,
  onPreview,
  onPublish,
  onSaveDraft,
  onBack,
}: {
  questionnaire: IntakeQuestionnaire;
  errors: ValidationError[];
  definitionName: string;
  saving: boolean;
  onPreview: () => void;
  onPublish: () => void;
  onSaveDraft: () => void;
  onBack: () => void;
}) {
  const isPublished = questionnaire.status === 'published';

  return (
    <div
      style={{
        height: 'var(--clinic-header-height)',
        padding: '20px var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexShrink: 0,
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
          }}
          title="Back to list"
        >
          <NavIcon name="arrow-left" />
        </button>
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              letterSpacing: '0.07px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {definitionName || 'Intake Form Builder'}
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Design and manage patient intake questionnaires
          </p>
        </div>

        {/* Status badge */}
        <span
          style={{
            height: 24,
            padding: '0 10px',
            borderRadius: 'var(--radius-full)',
            background: isPublished
              ? 'var(--color-success-bg, #f0fdf4)'
              : 'var(--color-warning-bg, #fffbeb)',
            border: isPublished
              ? '1px solid var(--color-success-border, #bbf7d0)'
              : '1px solid var(--color-warning-border, #fde68a)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: '22px',
            color: isPublished
              ? 'var(--color-success-text, #16a34a)'
              : 'var(--color-warning-text, #d97706)',
            whiteSpace: 'nowrap',
          }}
        >
          {isPublished ? 'Published' : 'Draft'}
        </span>

        {/* Version pill */}
        <span
          style={{
            height: 24,
            padding: '0 10px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: '24px',
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          {formatVersion(questionnaire.version)}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}>
        <Button variant="outline" size="md" onClick={onPreview}>
          Preview
        </Button>
        {!isPublished && (
          <Button
            variant="outline"
            size="md"
            disabled={saving}
            onClick={onSaveDraft}
            style={saving ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>
        )}
        <Button
          variant="gradient"
          size="md"
          disabled={errors.length > 0 || isPublished || saving}
          onClick={onPublish}
          style={
            errors.length > 0 || isPublished || saving
              ? { opacity: 0.5, cursor: 'not-allowed' }
              : undefined
          }
        >
          Publish
        </Button>
      </div>
    </div>
  );
}

// ─── ValidationBanner ───

function ValidationBanner({ errors }: { errors: ValidationError[] }) {
  if (errors.length === 0) return null;

  return (
    <div
      style={{
        background: 'var(--color-danger-bg, #fef2f2)',
        border: '1px solid var(--color-danger-border, #fecaca)',
        borderRadius: 'var(--radius-lg, 14px)',
        padding: 'var(--sp-4, 16px)',
        margin: '0 var(--sp-6) 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2, 8px)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-base, 14px)',
          fontWeight: 'var(--font-weight-semi, 600)',
          color: 'var(--color-danger-text, #dc2626)',
        }}
      >
        {errors.length} validation {errors.length === 1 ? 'error' : 'errors'} — fix before
        publishing
      </span>
      <ul
        style={{
          margin: 0,
          paddingLeft: 'var(--sp-5, 20px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1, 4px)',
        }}
      >
        {errors.map((err) => (
          <li
            key={err.id}
            style={{
              fontSize: 'var(--font-size-sm, 12px)',
              lineHeight: 'var(--line-height-sm, 16px)',
              color: 'var(--color-danger-text, #dc2626)',
            }}
          >
            {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── QuestionRow ───

function QuestionRow({
  question,
  isSelected,
  hasError,
  isDragOver,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  question: Question;
  questionIndex: number;
  isSelected: boolean;
  hasError: boolean;
  isDragOver: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <div
      draggable
      onClick={onSelect}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3, 12px)',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md, 8px)',
        background: isSelected ? 'var(--bg-primary-subtle, rgba(37,99,235,0.08))' : 'transparent',
        border: isSelected ? '1px solid var(--color-primary, #2563eb)' : '1px solid transparent',
        cursor: 'pointer',
        transition: 'background 100ms',
        opacity: 1,
        borderTop: isDragOver ? '2px solid var(--color-primary, #2563eb)' : undefined,
      }}
    >
      {/* Drag handle */}
      <span style={{ color: 'var(--text-placeholder, #9ca3af)', cursor: 'grab', flexShrink: 0 }}>
        <GripIcon />
      </span>

      {/* Type icon */}
      <span style={{ color: 'var(--text-muted, #6b7280)', flexShrink: 0 }}>
        <TypeIcon type={question.type} />
      </span>

      {/* Label + fieldKey */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-base, 14px)',
            fontWeight: 'var(--font-weight-medium, 500)',
            lineHeight: 'var(--line-height-base, 20px)',
            color: 'var(--text, #111827)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {question.label || '(untitled)'}
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-xs, 11px)',
            lineHeight: 'var(--line-height-xs, 14px)',
            color: 'var(--text-placeholder, #9ca3af)',
            fontFamily: 'monospace',
          }}
        >
          {question.fieldKey || '—'}
        </span>
      </div>

      {/* Badges */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)', flexShrink: 0 }}
      >
        {question.layout === 'full' && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 11px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              lineHeight: '18px',
              padding: '0 6px',
              borderRadius: 'var(--radius-xs, 4px)',
              background: 'var(--bg-surface-2, #f3f4f6)',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            Full
          </span>
        )}
        {(!question.layout || question.layout === 'half') && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 11px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              lineHeight: '18px',
              padding: '0 6px',
              borderRadius: 'var(--radius-xs, 4px)',
              background: 'var(--bg-surface-2, #f3f4f6)',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            ½
          </span>
        )}
        {question.required && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 11px)',
              fontWeight: 'var(--font-weight-medium, 500)',
              lineHeight: '18px',
              padding: '0 6px',
              borderRadius: 'var(--radius-xs, 4px)',
              background: 'var(--color-primary-soft, rgba(37,99,235,0.12))',
              color: 'var(--color-primary, #2563eb)',
            }}
          >
            Required
          </span>
        )}
        {hasError && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-danger-text, #dc2626)',
              flexShrink: 0,
            }}
          />
        )}
      </div>

      {/* Delete */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={smallBtnStyle}
        title="Remove question"
      >
        <NavIcon name="x" />
      </button>
    </div>
  );
}

// ─── BuilderSectionCard ───

function BuilderSectionCard({
  section,
  sectionIndex,
  selected,
  sectionHasError,
  questionErrorSet,
  onSelectSection,
  onSelectQuestion,
  onAddQuestion,
  onRemoveQuestion,
  onRemoveSection,
  onSectionDragStart,
  onSectionDragOver,
  onSectionDragEnd,
  onSectionDrop,
  isSectionDragOver,
  onQuestionDragStart,
  onQuestionDragOver,
  onQuestionDragEnd,
  onQuestionDrop,
  dragOverQuestionIndex,
}: {
  section: Section;
  sectionIndex: number;
  selected: Selected;
  sectionHasError: boolean;
  questionErrorSet: Set<string>;
  onSelectSection: () => void;
  onSelectQuestion: (questionId: string) => void;
  onAddQuestion: () => void;
  onRemoveQuestion: (questionId: string) => void;
  onRemoveSection: () => void;
  onSectionDragStart: (e: React.DragEvent) => void;
  onSectionDragOver: (e: React.DragEvent) => void;
  onSectionDragEnd: () => void;
  onSectionDrop: (e: React.DragEvent) => void;
  isSectionDragOver: boolean;
  onQuestionDragStart: (questionIndex: number, e: React.DragEvent) => void;
  onQuestionDragOver: (questionIndex: number, e: React.DragEvent) => void;
  onQuestionDragEnd: () => void;
  onQuestionDrop: (e: React.DragEvent) => void;
  dragOverQuestionIndex: number | null;
}) {
  const isSectionSelected = selected?.type === 'section' && selected.sectionId === section.id;

  return (
    <div
      draggable
      onDragStart={onSectionDragStart}
      onDragOver={onSectionDragOver}
      onDragEnd={onSectionDragEnd}
      onDrop={onSectionDrop}
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        border: isSectionSelected
          ? '1px solid var(--color-primary, #2563eb)'
          : '1px solid var(--border, #e5e7eb)',
        borderRadius: 'var(--radius-lg, 14px)',
        overflow: 'hidden',
        borderTop: isSectionDragOver ? '2px solid var(--color-primary, #2563eb)' : undefined,
      }}
    >
      {/* Section header */}
      <div
        onClick={onSelectSection}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3, 12px)',
          padding: 'var(--sp-4, 16px) var(--sp-5, 20px)',
          cursor: 'pointer',
          borderBottom: '1px solid var(--border, #e5e7eb)',
        }}
      >
        {/* Section drag handle */}
        <span style={{ color: 'var(--text-placeholder, #9ca3af)', cursor: 'grab', flexShrink: 0 }}>
          <GripIcon />
        </span>

        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: 'var(--radius-full)',
            background: isSectionSelected
              ? 'var(--color-primary, #2563eb)'
              : 'var(--bg-surface, #ffffff)',
            border: '1px solid var(--border, #e5e7eb)',
            color: isSectionSelected
              ? 'var(--text-on-primary, #fff)'
              : 'var(--text-muted, #6b7280)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-sm, 12px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            flexShrink: 0,
          }}
        >
          {sectionIndex + 1}
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-base, 14px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              lineHeight: 'var(--line-height-base, 20px)',
              color: 'var(--text, #111827)',
            }}
          >
            {section.title || '(untitled section)'}
          </div>
          {section.description && (
            <div
              style={{
                fontSize: 'var(--font-size-sm, 12px)',
                lineHeight: 'var(--line-height-sm, 16px)',
                color: 'var(--text-muted, #6b7280)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {section.description}
            </div>
          )}
        </div>

        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2, 8px)', flexShrink: 0 }}
        >
          {sectionHasError && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-danger-text, #dc2626)',
              }}
            />
          )}
          <span
            style={{
              fontSize: 'var(--font-size-xs, 11px)',
              lineHeight: '18px',
              padding: '0 6px',
              borderRadius: 'var(--radius-xs, 4px)',
              background: 'var(--bg-surface, #ffffff)',
              border: '1px solid var(--border, #e5e7eb)',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            {section.questions.length} {section.questions.length === 1 ? 'question' : 'questions'}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveSection();
            }}
            style={smallBtnStyle}
            title="Remove section"
          >
            <NavIcon name="x" />
          </button>
        </div>
      </div>

      {/* Questions list */}
      <div
        style={{
          padding: 'var(--sp-2, 8px) var(--sp-3, 12px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1, 4px)',
        }}
      >
        {section.questions.map((q, qIdx) => {
          const isQSelected =
            selected?.type === 'question' &&
            selected.sectionId === section.id &&
            selected.questionId === q.id;

          return (
            <QuestionRow
              key={q.id}
              question={q}
              questionIndex={qIdx}
              isSelected={isQSelected}
              hasError={questionErrorSet.has(q.id)}
              isDragOver={dragOverQuestionIndex === qIdx}
              onSelect={() => onSelectQuestion(q.id)}
              onRemove={() => onRemoveQuestion(q.id)}
              onDragStart={(e) => onQuestionDragStart(qIdx, e)}
              onDragOver={(e) => onQuestionDragOver(qIdx, e)}
              onDragEnd={onQuestionDragEnd}
              onDrop={onQuestionDrop}
            />
          );
        })}

        {/* Add Question button */}
        <button
          onClick={onAddQuestion}
          style={{
            width: '100%',
            height: 40,
            borderRadius: 'var(--radius-md, 8px)',
            border: '1px dashed var(--border, #e5e7eb)',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--sp-2, 8px)',
            fontSize: 'var(--font-size-sm, 12px)',
            fontWeight: 'var(--font-weight-medium, 500)',
            lineHeight: 'var(--line-height-sm, 16px)',
            color: 'var(--text-muted, #6b7280)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginTop: 'var(--sp-1, 4px)',
          }}
        >
          <NavIcon name="plus" /> Add Question
        </button>
      </div>
    </div>
  );
}

// ─── OptionsEditor ───

function OptionsEditor({
  options,
  onAdd,
  onUpdate,
  onRemove,
}: {
  options: Option[];
  onAdd: () => void;
  onUpdate: (optionId: string, field: 'label' | 'value', val: string) => void;
  onRemove: (optionId: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3, 12px)' }}>
      <label style={labelStyle}>Options</label>

      {options.map((opt) => (
        <div
          key={opt.id}
          style={{ display: 'flex', gap: 'var(--sp-2, 8px)', alignItems: 'center' }}
        >
          <input
            type="text"
            value={opt.label}
            onChange={(e) => onUpdate(opt.id, 'label', e.target.value)}
            placeholder="Label"
            style={{ ...inputStyle, flex: 1 }}
          />
          <input
            type="text"
            value={opt.value}
            onChange={(e) => onUpdate(opt.id, 'value', e.target.value)}
            placeholder="Value"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={() => onRemove(opt.id)} style={smallBtnStyle} title="Remove option">
            <NavIcon name="x" />
          </button>
        </div>
      ))}

      <button
        onClick={onAdd}
        style={{
          width: '100%',
          height: 36,
          borderRadius: 'var(--radius-md, 8px)',
          border: '1px dashed var(--border, #e5e7eb)',
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--sp-2, 8px)',
          fontSize: 'var(--font-size-sm, 12px)',
          fontWeight: 'var(--font-weight-medium, 500)',
          color: 'var(--text-muted, #6b7280)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <NavIcon name="plus" /> Add Option
      </button>
    </div>
  );
}

// ─── Validation Controls (shared) ───
// Imported from ../components/ValidationControls

// ─── PropertiesPanel ───

function PropertiesPanel({
  selected,
  questionnaire,
  validationFields,
  onUpdateSectionTitle,
  onUpdateSectionDescription,
  onUpdateQuestionField,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onUpdateFieldValidation,
}: {
  selected: Selected;
  questionnaire: IntakeQuestionnaire;
  validationFields: Record<string, FieldValidation>;
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  onUpdateSectionDescription: (sectionId: string, desc: string) => void;
  onUpdateQuestionField: (
    sectionId: string,
    questionId: string,
    field: string,
    value: string | boolean,
  ) => void;
  onAddOption: (sectionId: string, questionId: string) => void;
  onUpdateOption: (
    sectionId: string,
    questionId: string,
    optionId: string,
    field: 'label' | 'value',
    val: string,
  ) => void;
  onRemoveOption: (sectionId: string, questionId: string, optionId: string) => void;
  onUpdateFieldValidation: (fieldKey: string, fieldType: string, rules: Rule[]) => void;
}) {
  if (!selected) {
    return (
      <div
        style={{
          width: 320,
          flexShrink: 0,
          borderLeft: '1px solid var(--border, #e5e7eb)',
          background: 'var(--bg-surface, #ffffff)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--sp-6, 24px)',
        }}
      >
        <p
          style={{
            fontSize: 'var(--font-size-base, 14px)',
            lineHeight: 'var(--line-height-base, 20px)',
            color: 'var(--text-placeholder, #9ca3af)',
            textAlign: 'center',
          }}
        >
          Select a section or question to edit its properties
        </p>
      </div>
    );
  }

  if (selected.type === 'section') {
    const section = questionnaire.sections.find((s) => s.id === selected.sectionId);
    if (!section) return null;

    return (
      <div
        style={{
          width: 320,
          flexShrink: 0,
          borderLeft: '1px solid var(--border, #e5e7eb)',
          background: 'var(--bg-surface, #ffffff)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Panel header */}
        <div
          style={{
            padding: 'var(--sp-4, 16px) var(--sp-5, 20px)',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-base, 14px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              lineHeight: 'var(--line-height-base, 20px)',
              color: 'var(--text, #111827)',
            }}
          >
            Section Properties
          </span>
        </div>

        <div
          style={{
            padding: 'var(--sp-5, 20px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4, 16px)',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Title */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={section.title}
              onChange={(e) => onUpdateSectionTitle(section.id, e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={section.description ?? ''}
              onChange={(e) => onUpdateSectionDescription(section.id, e.target.value)}
              rows={3}
              style={textareaBaseStyle}
            />
          </div>
        </div>
      </div>
    );
  }

  // Question properties
  const section = questionnaire.sections.find((s) => s.id === selected.sectionId);
  const question = section?.questions.find((q) => q.id === selected.questionId);
  if (!section || !question) return null;

  const showOptions = question.type === 'select' || question.type === 'multi-select';

  return (
    <div
      style={{
        width: 320,
        flexShrink: 0,
        borderLeft: '1px solid var(--border, #e5e7eb)',
        background: 'var(--bg-surface, #ffffff)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: 'var(--sp-4, 16px) var(--sp-5, 20px)',
          borderBottom: '1px solid var(--border, #e5e7eb)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-base, 14px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            lineHeight: 'var(--line-height-base, 20px)',
            color: 'var(--text, #111827)',
          }}
        >
          Question Properties
        </span>
      </div>

      <div
        style={{
          padding: 'var(--sp-5, 20px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4, 16px)',
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {/* Label */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={labelStyle}>Label</label>
          <input
            type="text"
            value={question.label}
            onChange={(e) =>
              onUpdateQuestionField(section.id, question.id, 'label', e.target.value)
            }
            style={inputStyle}
          />
        </div>

        {/* Field Key */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={labelStyle}>Field Key</label>
          <input
            type="text"
            value={question.fieldKey}
            onChange={(e) =>
              onUpdateQuestionField(section.id, question.id, 'fieldKey', e.target.value)
            }
            placeholder="snake_case"
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
        </div>

        {/* Type */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={labelStyle}>Type</label>
          <select
            value={question.type}
            onChange={(e) => onUpdateQuestionField(section.id, question.id, 'type', e.target.value)}
            style={inputStyle}
          >
            {FIELD_TYPES.map((ft) => (
              <option key={ft.value} value={ft.value}>
                {ft.label}
              </option>
            ))}
          </select>
        </div>

        {/* Required toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={labelStyle}>Required</label>
          <button
            onClick={() =>
              onUpdateQuestionField(section.id, question.id, 'required', !question.required)
            }
            style={{
              width: 44,
              height: 24,
              borderRadius: 12,
              border: 'none',
              background: question.required
                ? 'var(--color-primary, #2563eb)'
                : 'var(--bg-surface-2, #e5e7eb)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 150ms',
              padding: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: question.required ? 22 : 2,
                width: 20,
                height: 20,
                borderRadius: 'var(--radius-full)',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'left 150ms',
              }}
            />
          </button>
        </div>

        {/* Help Text */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={labelStyle}>Help Text</label>
          <input
            type="text"
            value={question.helpText ?? ''}
            onChange={(e) =>
              onUpdateQuestionField(section.id, question.id, 'helpText', e.target.value)
            }
            placeholder="Optional help text"
            style={inputStyle}
          />
        </div>

        {/* Width */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={labelStyle}>Width</label>
          <div style={{ display: 'flex', gap: 0 }}>
            {(['half', 'full'] as const).map((val) => {
              const active = (question.layout ?? 'half') === val;
              return (
                <button
                  key={val}
                  onClick={() => onUpdateQuestionField(section.id, question.id, 'layout', val)}
                  style={{
                    flex: 1,
                    height: 'var(--input-height, 42px)',
                    border: '1px solid var(--border, #e5e7eb)',
                    background: active
                      ? 'var(--color-primary, #2563eb)'
                      : 'var(--input-bg, #ffffff)',
                    color: active ? '#fff' : 'var(--text, #111827)',
                    fontSize: 'var(--font-size-base, 14px)',
                    fontWeight: 'var(--font-weight-medium, 500)',
                    cursor: 'pointer',
                    borderRadius:
                      val === 'half'
                        ? 'var(--input-radius, 10px) 0 0 var(--input-radius, 10px)'
                        : '0 var(--input-radius, 10px) var(--input-radius, 10px) 0',
                    marginLeft: val === 'full' ? -1 : 0,
                  }}
                >
                  {val === 'half' ? 'Half' : 'Full'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Options editor (for select/multi-select) */}
        {showOptions && (
          <OptionsEditor
            options={question.options ?? []}
            onAdd={() => onAddOption(section.id, question.id)}
            onUpdate={(optId, field, val) =>
              onUpdateOption(section.id, question.id, optId, field, val)
            }
            onRemove={(optId) => onRemoveOption(section.id, question.id, optId)}
          />
        )}

        {/* Guided validation controls */}
        <div
          style={{
            borderTop: '1px solid var(--border, #e5e7eb)',
            paddingTop: 'var(--sp-4, 16px)',
            marginTop: 'var(--sp-1, 4px)',
          }}
        >
          <ValidationControls
            question={question}
            fieldValidation={validationFields[question.fieldKey]}
            onUpdate={onUpdateFieldValidation}
          />
        </div>
      </div>
    </div>
  );
}

// ─── PublishedBanner ───

function PublishedBanner({ onEditAsDraft }: { onEditAsDraft: () => void }) {
  return (
    <div
      style={{
        background: 'var(--color-success-bg, #f0fdf4)',
        border: '1px solid var(--color-success-border, #bbf7d0)',
        borderRadius: 'var(--radius-lg, 14px)',
        padding: 'var(--sp-5, 20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4, 16px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
        <span style={{ color: 'var(--color-success-text, #16a34a)' }}>
          <NavIcon name="check-circle" />
        </span>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base, 14px)',
              fontWeight: 'var(--font-weight-semi, 600)',
              lineHeight: 'var(--line-height-base, 20px)',
              color: 'var(--color-success-text, #16a34a)',
            }}
          >
            This questionnaire is published
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm, 12px)',
              lineHeight: 'var(--line-height-sm, 16px)',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            Create a new draft to make changes
          </div>
        </div>
      </div>
      <Button variant="outline" size="md" onClick={onEditAsDraft}>
        Edit as Draft
      </Button>
    </div>
  );
}

// ─── PreviewModal ───

function PreviewModal({
  open,
  onClose,
  questionnaire,
  brandTokens,
}: {
  open: boolean;
  onClose: () => void;
  questionnaire: IntakeQuestionnaire;
  brandTokens: Record<string, string>;
}) {
  const [previewAnswers] = useState<IntakeAnswers>({});

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Form Preview"
      width={520}
      portalStyle={brandTokens as CSSProperties}
      badges={
        <>
          <DrawerBadge label={formatVersion(questionnaire.version)} variant="neutral" />
          <DrawerBadge
            label={questionnaire.status === 'published' ? 'Published' : 'Draft'}
            variant={questionnaire.status === 'published' ? 'neutral' : 'warning'}
          />
        </>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <IntakeRenderer
        questionnaire={questionnaire}
        value={previewAnswers}
        onChange={() => {}}
        mode="preview"
        errors={[]}
      />
    </Drawer>
  );
}

// ─── Status Badge Styles ───

const STATUS_STYLES: Record<string, { bg: string; border: string; color: string }> = {
  draft: {
    bg: 'var(--color-warning-bg, #fffbeb)',
    border: '1px solid var(--color-warning-border, #fde68a)',
    color: 'var(--color-warning-text, #d97706)',
  },
  published: {
    bg: 'var(--color-success-bg, #f0fdf4)',
    border: '1px solid var(--color-success-border, #bbf7d0)',
    color: 'var(--color-success-text, #16a34a)',
  },
  archived: {
    bg: 'var(--bg-surface-2, #f9fafb)',
    border: '1px solid var(--border, #e5e7eb)',
    color: 'var(--text-muted, #6b7280)',
  },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.draft;
  return (
    <span
      style={{
        height: 24,
        padding: '0 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        border: s.border,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '22px',
        color: s.color,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

// ─── Definitions List View ───

const LIST_COL_TEMPLATE = '1fr 120px 80px 120px';

function DefinitionsListView({
  definitions,
  onCreateDraft,
  onEdit,
  onView,
}: {
  definitions: IntakeFormDefinitionVM[];
  onCreateDraft: () => void;
  onEdit: (id: string) => void;
  onView: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              letterSpacing: '0.07px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Intake Form Definitions
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Manage patient intake questionnaires
          </p>
        </div>
        <Button variant="gradient" size="md" onClick={onCreateDraft}>
          Create Draft
        </Button>
      </div>

      {/* Table header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: LIST_COL_TEMPLATE,
          padding: 'var(--sp-3) var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface-2, #f9fafb)',
        }}
      >
        {['Name', 'Status', 'Version', 'Actions'].map((col) => (
          <span
            key={col}
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {col}
          </span>
        ))}
      </div>

      {/* Table body */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {definitions.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-placeholder)',
              fontSize: 'var(--font-size-base)',
            }}
          >
            No intake form definitions yet. Click "Create Draft" to get started.
          </div>
        ) : (
          definitions.map((def) => (
            <div
              key={def.id}
              style={{
                display: 'grid',
                gridTemplateColumns: LIST_COL_TEMPLATE,
                padding: 'var(--sp-4) var(--sp-6)',
                borderBottom: '1px solid var(--border)',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {def.name}
              </span>
              <StatusBadge status={def.status} />
              <span
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--text-muted)',
                }}
              >
                {formatVersion(def.version)}
              </span>
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                {def.status === 'draft' ? (
                  <Button variant="outline" size="sm" onClick={() => onEdit(def.id)}>
                    Edit
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => onView(def.id)}>
                    View
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-6)',
          borderTop: '1px solid var(--border)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {definitions.length} {definitions.length === 1 ? 'definition' : 'definitions'}
      </div>
    </div>
  );
}

// ─── Definition Editor View ───

function DefinitionEditorView({
  definitionId,
  onBack,
}: {
  definitionId: string;
  onBack: () => void;
}) {
  const { db } = usePowerSyncDb();
  const brandTokens = useBrandTokens();

  // Load from PowerSync
  const { data: rows = [] } = usePowerSyncQuery<IntakeFormDefinitionRow>(
    qIntakeFormDefinitionById(definitionId),
  );

  const definition = useMemo(() => {
    if (rows.length === 0) return null;
    return buildIntakeDefinitionVM(rows[0]);
  }, [rows]);

  // Local state for questionnaire
  const [questionnaire, setQuestionnaire] = useState<IntakeQuestionnaire | null>(null);
  const [definitionName, setDefinitionName] = useState('');
  const [selected, setSelected] = useState<Selected>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationFields, setValidationFields] = useState<Record<string, FieldValidation>>({});

  // Initialize from DB on first load
  useEffect(() => {
    if (definition && questionnaire === null) {
      setQuestionnaire(definition.schema as unknown as IntakeQuestionnaire);
      setDefinitionName(definition.name);
      const vsRaw = definition.validationSchema as ValidationSchema | null;
      if (vsRaw && typeof vsRaw === 'object' && vsRaw.fields) {
        setValidationFields(vsRaw.fields);
      }
    }
  }, [definition, questionnaire]);

  // Derived
  const errors = useMemo(
    () => (questionnaire ? validateQuestionnaire(questionnaire) : []),
    [questionnaire],
  );

  // Serialize validation fields → schema object (or null if empty)
  const validationSchemaObj = useMemo((): ValidationSchema | null => {
    const keys = Object.keys(validationFields);
    if (keys.length === 0) return null;
    // Filter out fields with no rules
    const filtered: Record<string, FieldValidation> = {};
    for (const key of keys) {
      if (validationFields[key].rules.length > 0) {
        filtered[key] = validationFields[key];
      }
    }
    if (Object.keys(filtered).length === 0) return null;
    return { version: 1, fields: filtered };
  }, [validationFields]);

  const validationJsonPreview = useMemo(
    () => (validationSchemaObj ? JSON.stringify(validationSchemaObj, null, 2) : ''),
    [validationSchemaObj],
  );

  // Update validation rules for a specific field
  const updateFieldValidation = useCallback(
    (fieldKey: string, fieldType: string, rules: Rule[]) => {
      setValidationFields((prev) => {
        if (rules.length === 0) {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        }
        return { ...prev, [fieldKey]: { type: fieldType as FieldValidation['type'], rules } };
      });
    },
    [],
  );

  const sectionErrorSet = useMemo(() => {
    const set = new Set<string>();
    for (const err of errors) {
      if (err.sectionId) set.add(err.sectionId);
    }
    return set;
  }, [errors]);

  const questionErrorSet = useMemo(() => {
    const set = new Set<string>();
    for (const err of errors) {
      if (err.questionId) set.add(err.questionId);
    }
    return set;
  }, [errors]);

  // Mutation helpers
  const touch = useCallback(
    (q: IntakeQuestionnaire): IntakeQuestionnaire => ({ ...q, lastModified: now() }),
    [],
  );

  const updateSectionTitle = useCallback(
    (sectionId: string, title: string) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) => (s.id === sectionId ? { ...s, title } : s)),
            })
          : q,
      );
    },
    [touch],
  );

  const updateSectionDescription = useCallback(
    (sectionId: string, description: string) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) => (s.id === sectionId ? { ...s, description } : s)),
            })
          : q,
      );
    },
    [touch],
  );

  const addSection = useCallback(() => {
    const newSection: Section = {
      id: uid('s'),
      title: '',
      description: '',
      questions: [],
      collapsed: false,
    };
    setQuestionnaire((q) => (q ? touch({ ...q, sections: [...q.sections, newSection] }) : q));
    setSelected({ type: 'section', sectionId: newSection.id });
  }, [touch]);

  const removeSection = useCallback(
    (sectionId: string) => {
      setQuestionnaire((q) =>
        q ? touch({ ...q, sections: q.sections.filter((s) => s.id !== sectionId) }) : q,
      );
      setSelected((sel) => {
        if (!sel) return null;
        if (sel.type === 'section' && sel.sectionId === sectionId) return null;
        if (sel.type === 'question' && sel.sectionId === sectionId) return null;
        return sel;
      });
    },
    [touch],
  );

  const updateQuestionField = useCallback(
    (sectionId: string, questionId: string, field: string, value: string | boolean) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId
                  ? {
                      ...s,
                      questions: s.questions.map((qn) =>
                        qn.id === questionId ? { ...qn, [field]: value } : qn,
                      ),
                    }
                  : s,
              ),
            })
          : q,
      );
    },
    [touch],
  );

  const addQuestion = useCallback(
    (sectionId: string) => {
      const newQ: Question = {
        id: uid('q'),
        label: '',
        fieldKey: '',
        type: 'text',
        required: false,
      };
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId ? { ...s, questions: [...s.questions, newQ] } : s,
              ),
            })
          : q,
      );
      setSelected({ type: 'question', sectionId, questionId: newQ.id });
    },
    [touch],
  );

  const removeQuestion = useCallback(
    (sectionId: string, questionId: string) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId
                  ? { ...s, questions: s.questions.filter((qn) => qn.id !== questionId) }
                  : s,
              ),
            })
          : q,
      );
      setSelected((sel) => {
        if (
          sel?.type === 'question' &&
          sel.sectionId === sectionId &&
          sel.questionId === questionId
        )
          return null;
        return sel;
      });
    },
    [touch],
  );

  const addOption = useCallback(
    (sectionId: string, questionId: string) => {
      const newOpt: Option = { id: uid('opt'), label: '', value: '' };
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId
                  ? {
                      ...s,
                      questions: s.questions.map((qn) =>
                        qn.id === questionId
                          ? { ...qn, options: [...(qn.options ?? []), newOpt] }
                          : qn,
                      ),
                    }
                  : s,
              ),
            })
          : q,
      );
    },
    [touch],
  );

  const updateOption = useCallback(
    (
      sectionId: string,
      questionId: string,
      optionId: string,
      field: 'label' | 'value',
      val: string,
    ) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId
                  ? {
                      ...s,
                      questions: s.questions.map((qn) =>
                        qn.id === questionId
                          ? {
                              ...qn,
                              options: (qn.options ?? []).map((o) =>
                                o.id === optionId ? { ...o, [field]: val } : o,
                              ),
                            }
                          : qn,
                      ),
                    }
                  : s,
              ),
            })
          : q,
      );
    },
    [touch],
  );

  const removeOption = useCallback(
    (sectionId: string, questionId: string, optionId: string) => {
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) =>
                s.id === sectionId
                  ? {
                      ...s,
                      questions: s.questions.map((qn) =>
                        qn.id === questionId
                          ? { ...qn, options: (qn.options ?? []).filter((o) => o.id !== optionId) }
                          : qn,
                      ),
                    }
                  : s,
              ),
            })
          : q,
      );
    },
    [touch],
  );

  // Reorder helpers
  const moveSection = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      setQuestionnaire((q) => {
        if (!q) return q;
        const sections = [...q.sections];
        const [moved] = sections.splice(fromIndex, 1);
        sections.splice(toIndex, 0, moved);
        return touch({ ...q, sections });
      });
    },
    [touch],
  );

  const moveQuestion = useCallback(
    (sectionId: string, fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      setQuestionnaire((q) =>
        q
          ? touch({
              ...q,
              sections: q.sections.map((s) => {
                if (s.id !== sectionId) return s;
                const questions = [...s.questions];
                const [moved] = questions.splice(fromIndex, 1);
                questions.splice(toIndex, 0, moved);
                return { ...s, questions };
              }),
            })
          : q,
      );
    },
    [touch],
  );

  // Section DnD state
  const dragSectionRef = useRef<number | null>(null);
  const [dropSectionIndex, setDropSectionIndex] = useState<number | null>(null);

  const handleSectionDragStart = useCallback((index: number, e: React.DragEvent) => {
    dragSectionRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `section:${index}`);
    e.stopPropagation();
  }, []);

  const handleSectionDragOver = useCallback((index: number, e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('text/plain') || dragSectionRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDropSectionIndex(index !== dragSectionRef.current ? index : null);
  }, []);

  const handleSectionDrop = useCallback(
    (targetIndex: number, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const from = dragSectionRef.current;
      if (from !== null && from !== targetIndex) moveSection(from, targetIndex);
      dragSectionRef.current = null;
      setDropSectionIndex(null);
    },
    [moveSection],
  );

  const handleSectionDragEnd = useCallback(() => {
    dragSectionRef.current = null;
    setDropSectionIndex(null);
  }, []);

  // Question DnD state
  const dragQuestionRef = useRef<{ sectionId: string; index: number } | null>(null);
  const [dropQuestionTarget, setDropQuestionTarget] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);

  const handleQuestionDragStart = useCallback(
    (sectionId: string, questionIndex: number, e: React.DragEvent) => {
      dragQuestionRef.current = { sectionId, index: questionIndex };
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `question:${sectionId}:${questionIndex}`);
      e.stopPropagation();
    },
    [],
  );

  const handleQuestionDragOver = useCallback(
    (sectionId: string, questionIndex: number, e: React.DragEvent) => {
      const src = dragQuestionRef.current;
      if (!src || src.sectionId !== sectionId) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDropQuestionTarget(
        questionIndex !== src.index ? { sectionId, index: questionIndex } : null,
      );
    },
    [],
  );

  const handleQuestionDrop = useCallback(
    (sectionId: string, e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const src = dragQuestionRef.current;
      const tgt = dropQuestionTarget;
      if (src && tgt && src.sectionId === sectionId && src.index !== tgt.index) {
        moveQuestion(sectionId, src.index, tgt.index);
      }
      dragQuestionRef.current = null;
      setDropQuestionTarget(null);
    },
    [dropQuestionTarget, moveQuestion],
  );

  const handleQuestionDragEnd = useCallback(() => {
    dragQuestionRef.current = null;
    setDropQuestionTarget(null);
  }, []);

  // Save Draft (PowerSync)
  const handleSaveDraft = useCallback(async () => {
    if (!db || !questionnaire) return;
    setSaving(true);
    try {
      const data = serializeIntakeDefinitionForWrite(
        definitionId,
        definitionName,
        questionnaire.status,
        questionnaire.version,
        questionnaire as unknown as Record<string, unknown>,
        validationSchemaObj as Record<string, unknown> | null,
      );
      // PowerSync views don't support UPSERT — try UPDATE first, INSERT if row is new
      const existing = await db.getOptional(`SELECT id FROM intake_form_definitions WHERE id = ?`, [
        data.id,
      ]);
      if (existing) {
        await db.execute(
          `UPDATE intake_form_definitions SET name = ?, status = ?, version = ?, schema = ?, validation_schema = ? WHERE id = ?`,
          [data.name, data.status, data.version, data.schema, data.validation_schema, data.id],
        );
      } else {
        await db.execute(
          `INSERT INTO intake_form_definitions (id, name, status, version, schema, validation_schema) VALUES (?, ?, ?, ?, ?, ?)`,
          [data.id, data.name, data.status, data.version, data.schema, data.validation_schema],
        );
      }
    } catch (err) {
      console.error('[IntakeFormBuilder] Save draft failed:', err);
    } finally {
      setSaving(false);
    }
  }, [db, questionnaire, definitionId, definitionName, validationSchemaObj]);

  // Publish (PowerSync)
  const handlePublish = useCallback(async () => {
    if (!db || !questionnaire || errors.length > 0) return;
    setSaving(true);
    try {
      // Archive any previously published definition with the same name
      await db.execute(
        `UPDATE intake_form_definitions SET status = 'archived' WHERE name = ? AND status = 'published' AND id != ?`,
        [definitionName, definitionId],
      );
      // Update current definition to published
      const publishedQ: IntakeQuestionnaire = {
        ...questionnaire,
        status: 'published',
        publishedDate: now(),
      };
      const data = serializeIntakeDefinitionForWrite(
        definitionId,
        definitionName,
        'published',
        questionnaire.version,
        publishedQ as unknown as Record<string, unknown>,
        validationSchemaObj as Record<string, unknown> | null,
      );
      // PowerSync views don't support UPSERT — try UPDATE first, INSERT if row is new
      const existing = await db.getOptional(`SELECT id FROM intake_form_definitions WHERE id = ?`, [
        data.id,
      ]);
      if (existing) {
        await db.execute(
          `UPDATE intake_form_definitions SET name = ?, status = ?, version = ?, schema = ?, validation_schema = ? WHERE id = ?`,
          [data.name, data.status, data.version, data.schema, data.validation_schema, data.id],
        );
      } else {
        await db.execute(
          `INSERT INTO intake_form_definitions (id, name, status, version, schema, validation_schema) VALUES (?, ?, ?, ?, ?, ?)`,
          [data.id, data.name, data.status, data.version, data.schema, data.validation_schema],
        );
      }
      setQuestionnaire(publishedQ);
    } catch (err) {
      console.error('[IntakeFormBuilder] Publish failed:', err);
    } finally {
      setSaving(false);
    }
  }, [db, questionnaire, errors, validationSchemaObj, definitionId, definitionName]);

  const handleEditAsDraft = useCallback(() => {
    setQuestionnaire((q) =>
      q ? touch({ ...q, status: 'draft', version: versionStamp(), publishedDate: undefined }) : q,
    );
  }, [touch]);

  if (!questionnaire) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
      >
        <span style={{ color: 'var(--text-muted)' }}>Loading…</span>
      </div>
    );
  }

  const isPublished = questionnaire.status === 'published';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BuilderHeader
        questionnaire={questionnaire}
        errors={errors}
        definitionName={definitionName}
        saving={saving}
        onPreview={() => setPreviewOpen(true)}
        onPublish={handlePublish}
        onSaveDraft={handleSaveDraft}
        onBack={onBack}
      />

      {!isPublished && errors.length > 0 && (
        <div style={{ padding: 'var(--sp-4, 16px) var(--sp-6, 24px) 0' }}>
          <ValidationBanner errors={errors} />
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            padding: 'var(--sp-5, 20px) var(--sp-6, 24px)',
          }}
        >
          {isPublished ? (
            <PublishedBanner onEditAsDraft={handleEditAsDraft} />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-4, 16px)',
                maxWidth: 720,
              }}
            >
              {questionnaire.sections.map((section, idx) => (
                <BuilderSectionCard
                  key={section.id}
                  section={section}
                  sectionIndex={idx}
                  selected={selected}
                  sectionHasError={sectionErrorSet.has(section.id)}
                  questionErrorSet={questionErrorSet}
                  onSelectSection={() => setSelected({ type: 'section', sectionId: section.id })}
                  onSelectQuestion={(qId) =>
                    setSelected({ type: 'question', sectionId: section.id, questionId: qId })
                  }
                  onAddQuestion={() => addQuestion(section.id)}
                  onRemoveQuestion={(qId) => removeQuestion(section.id, qId)}
                  onRemoveSection={() => removeSection(section.id)}
                  onSectionDragStart={(e) => handleSectionDragStart(idx, e)}
                  onSectionDragOver={(e) => handleSectionDragOver(idx, e)}
                  onSectionDragEnd={handleSectionDragEnd}
                  onSectionDrop={(e) => handleSectionDrop(idx, e)}
                  isSectionDragOver={dropSectionIndex === idx}
                  onQuestionDragStart={(qIdx, e) => handleQuestionDragStart(section.id, qIdx, e)}
                  onQuestionDragOver={(qIdx, e) => handleQuestionDragOver(section.id, qIdx, e)}
                  onQuestionDragEnd={handleQuestionDragEnd}
                  onQuestionDrop={(e) => handleQuestionDrop(section.id, e)}
                  dragOverQuestionIndex={
                    dropQuestionTarget?.sectionId === section.id ? dropQuestionTarget.index : null
                  }
                />
              ))}
              <button
                onClick={addSection}
                style={{
                  width: '100%',
                  height: 52,
                  borderRadius: 'var(--radius-lg, 14px)',
                  border: '1px dashed var(--border, #e5e7eb)',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--sp-2, 8px)',
                  fontSize: 'var(--font-size-base, 14px)',
                  fontWeight: 'var(--font-weight-medium, 500)',
                  lineHeight: 'var(--line-height-base, 20px)',
                  letterSpacing: '-0.15px',
                  color: 'var(--text-muted, #6b7280)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <NavIcon name="plus" /> Add Section
              </button>

              {/* ─── Validation JSON Preview (read-only) ─── */}
              {validationJsonPreview && (
                <div
                  style={{
                    background: 'var(--bg-surface-2, #f9fafb)',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: 'var(--radius-lg, 14px)',
                    padding: 'var(--sp-5, 20px)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-3, 12px)',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 'var(--font-size-lg, 16px)',
                      fontWeight: 'var(--font-weight-semi, 600)',
                      lineHeight: 'var(--line-height-lg, 24px)',
                      letterSpacing: '-0.31px',
                      color: 'var(--text, #111827)',
                    }}
                  >
                    Validation Rules (JSON Preview)
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 'var(--font-size-sm, 12px)',
                      lineHeight: 'var(--line-height-sm, 16px)',
                      color: 'var(--text-muted, #6b7280)',
                    }}
                  >
                    Generated from the guided controls in the Properties panel. Read-only.
                  </p>
                  <pre
                    style={{
                      margin: 0,
                      padding: 'var(--sp-3, 12px)',
                      background: 'var(--bg-surface, #ffffff)',
                      border: '1px solid var(--border, #e5e7eb)',
                      borderRadius: 'var(--input-radius, 10px)',
                      fontSize: 'var(--font-size-sm, 12px)',
                      lineHeight: '1.5',
                      overflow: 'auto',
                      whiteSpace: 'pre',
                      color: 'var(--text-muted, #6b7280)',
                      maxHeight: 240,
                    }}
                  >
                    {validationJsonPreview}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {!isPublished && (
          <PropertiesPanel
            selected={selected}
            questionnaire={questionnaire}
            validationFields={validationFields}
            onUpdateSectionTitle={updateSectionTitle}
            onUpdateSectionDescription={updateSectionDescription}
            onUpdateQuestionField={updateQuestionField}
            onAddOption={addOption}
            onUpdateOption={updateOption}
            onRemoveOption={removeOption}
            onUpdateFieldValidation={updateFieldValidation}
          />
        )}
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        questionnaire={questionnaire}
        brandTokens={brandTokens}
      />
    </div>
  );
}

// ─── Definition Viewer View ───

function DefinitionViewerView({
  definitionId,
  onBack,
  onEditAsDraft,
}: {
  definitionId: string;
  onBack: () => void;
  onEditAsDraft: (newDraftId: string) => void;
}) {
  const { db } = usePowerSyncDb();

  const { data: rows = [] } = usePowerSyncQuery<IntakeFormDefinitionRow>(
    qIntakeFormDefinitionById(definitionId),
  );

  const definition = useMemo(() => {
    if (rows.length === 0) return null;
    return buildIntakeDefinitionVM(rows[0]);
  }, [rows]);

  const questionnaire = useMemo(
    () => (definition ? (definition.schema as unknown as IntakeQuestionnaire) : null),
    [definition],
  );

  const [previewAnswers] = useState<IntakeAnswers>({});

  const handleEditAsDraft = useCallback(async () => {
    if (!db || !definition || !questionnaire) return;
    const newId = crypto.randomUUID();
    const newVersion = versionStamp();
    const newQ: IntakeQuestionnaire = {
      ...questionnaire,
      id: newId,
      status: 'draft',
      version: newVersion,
      lastModified: now(),
      publishedDate: undefined,
    };
    const data = serializeIntakeDefinitionForWrite(
      newId,
      definition.name,
      'draft',
      newVersion,
      newQ as unknown as Record<string, unknown>,
      definition.validationSchema,
    );
    await db.execute(
      `INSERT INTO intake_form_definitions (id, name, status, version, schema, validation_schema)
       VALUES (?, ?, ?, ?, ?, ?)`,
      [data.id, data.name, data.status, data.version, data.schema, data.validation_schema],
    );
    onEditAsDraft(newId);
  }, [db, definition, questionnaire, onEditAsDraft]);

  if (!definition || !questionnaire) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
      >
        <span style={{ color: 'var(--text-muted)' }}>Loading…</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
            }}
            title="Back to list"
          >
            <NavIcon name="arrow-left" />
          </button>
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl)',
                letterSpacing: '0.07px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              {definition.name}
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                lineHeight: 'var(--line-height-base)',
                color: 'var(--text-muted)',
                margin: '4px 0 0',
              }}
            >
              Published intake form preview
            </p>
          </div>
          <StatusBadge status={definition.status} />
          <span
            style={{
              height: 24,
              padding: '0 10px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: '24px',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatVersion(definition.version)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-5, 20px) var(--sp-6, 24px)' }}>
        <div
          style={{
            maxWidth: 720,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5, 20px)',
          }}
        >
          <PublishedBanner onEditAsDraft={handleEditAsDraft} />
          <IntakeRenderer
            questionnaire={questionnaire}
            value={previewAnswers}
            onChange={() => {}}
            mode="preview"
            errors={[]}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main Page (Orchestrator) ───

export function IntakeFormBuilderPage() {
  useOutletContext<{ collapsed: boolean }>();
  const { db } = usePowerSyncDb();

  const [view, setView] = useState<ViewState>({ mode: 'list' });

  // Fetch all definitions
  const { data: allRows = [] } =
    usePowerSyncQuery<IntakeFormDefinitionRow>(qIntakeFormDefinitions());

  const definitions = useMemo(() => allRows.map(buildIntakeDefinitionVM), [allRows]);

  // Create new draft
  const handleCreateDraft = useCallback(async () => {
    if (!db) return;
    const newId = crypto.randomUUID();
    const newVersion = versionStamp();
    const newQ: IntakeQuestionnaire = {
      id: newId,
      status: 'draft',
      version: newVersion,
      sections: [],
      lastModified: now(),
    };
    const data = serializeIntakeDefinitionForWrite(
      newId,
      'Patient Intake',
      'draft',
      newVersion,
      newQ as unknown as Record<string, unknown>,
      null,
    );
    await db.execute(
      `INSERT INTO intake_form_definitions (id, name, status, version, schema, validation_schema)
       VALUES (?, ?, ?, ?, ?, ?)`,
      [data.id, data.name, data.status, data.version, data.schema, data.validation_schema],
    );
    setView({ mode: 'edit', definitionId: newId });
  }, [db]);

  switch (view.mode) {
    case 'list':
      return (
        <DefinitionsListView
          definitions={definitions}
          onCreateDraft={handleCreateDraft}
          onEdit={(id) => setView({ mode: 'edit', definitionId: id })}
          onView={(id) => setView({ mode: 'view', definitionId: id })}
        />
      );
    case 'edit':
      return (
        <DefinitionEditorView
          definitionId={view.definitionId}
          onBack={() => setView({ mode: 'list' })}
        />
      );
    case 'view':
      return (
        <DefinitionViewerView
          definitionId={view.definitionId}
          onBack={() => setView({ mode: 'list' })}
          onEditAsDraft={(newDraftId) => setView({ mode: 'edit', definitionId: newDraftId })}
        />
      );
  }
}
